import React, { useState, useEffect } from 'react';
import {
  Modal,
  TextInput,
  NumberInput,
  MultiSelect,
  Select,
  Switch,
  Button,
  Stack,
  Group,
  Title,
  Text,
  Card,
  Alert
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconInfoCircle, IconUsers, IconClock } from '@tabler/icons-react';
import { CrearSalaMultijugadorDTO } from '../../types/multijugador';
import { especialidadService } from '../../services/especialidadService';
import { Especialidad } from '../../Models/Especialidad';
import { Dificultad } from '../../Models/Dificultad';

interface CrearSalaModalProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (datos: CrearSalaMultijugadorDTO) => void;
  loading: boolean;
}

const CrearSalaModal: React.FC<CrearSalaModalProps> = ({
  opened,
  onClose,
  onSubmit,
  loading
}) => {
  const [especialidades, setEspecialidades] = useState<Especialidad[]>([]);
  const [dificultades] = useState<Dificultad[]>([
    { id: 1, nombre: 'Fácil' },
    { id: 2, nombre: 'Intermedio' },
    { id: 3, nombre: 'Difícil' }
  ]);

  const form = useForm({
    initialValues: {
      nombreSala: '',
      cantidadReactivos: 20,
      maxParticipantes: 5,
      tiempoLimiteMin: 30,
      permitirEntradaTardia: false,
      especialidadIds: [] as string[],
      dificultadId: '2'
    },
    validate: {
      nombreSala: (value: string) =>
        value.trim().length < 3 ? 'El nombre debe tener al menos 3 caracteres' : null,
      cantidadReactivos: (value: number) =>
        value < 5 || value > 50 ? 'Debe ser entre 5 y 50 preguntas' : null,
      maxParticipantes: (value: number) =>
        value < 2 || value > 10 ? 'Debe ser entre 2 y 10 participantes' : null,
      tiempoLimiteMin: (value: number) =>
        value < 10 || value > 120 ? 'Debe ser entre 10 y 120 minutos' : null,
      especialidadIds: (value: string[]) =>
        value.length === 0 ? 'Selecciona al menos una especialidad' : null,
    }
  });

  useEffect(() => {
    if (opened) {
      cargarEspecialidades();
    }
  }, [opened]);

  const cargarEspecialidades = async () => {
    try {
      const data = await especialidadService.listar();
      setEspecialidades(data);
    } catch (error) {
      console.error('Error al cargar especialidades:', error);
    }
  };

  const handleSubmit = (values: typeof form.values) => {
    const datos: CrearSalaMultijugadorDTO = {
      nombreSala: values.nombreSala.trim(),
      cantidadReactivos: values.cantidadReactivos,
      maxParticipantes: values.maxParticipantes,
      tiempoLimiteMin: values.tiempoLimiteMin,
      permitirEntradaTardia: values.permitirEntradaTardia,
      especialidadIds: values.especialidadIds.map(id => parseInt(id)),
      dificultadId: parseInt(values.dificultadId)
    };

    onSubmit(datos);
  };

  const especialidadOptions = especialidades.map(esp => ({
    value: esp.id.toString(),
    label: esp.nombre
  }));

  const dificultadOptions = dificultades.map(dif => ({
    value: dif.id.toString(),
    label: dif.nombre
  }));

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          <IconUsers size={24} className="text-blue-500" />
          <Title order={3}>Crear Nueva Sala</Title>
        </div>
      }
      size="lg"
      centered
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          {/* Información de la sala */}
          <Card withBorder padding="sm" className="bg-blue-50">
            <Alert 
              icon={<IconInfoCircle size={16} />}
              color="blue"
              variant="light"
            >
              Serás el <strong>creador</strong> de la sala y podrás iniciar el examen cuando todos estén listos.
            </Alert>
          </Card>

          {/* Configuración básica */}
          <TextInput
            label="Nombre de la Sala"
            placeholder="Ej: Cardiología Avanzada"
            description="Este nombre será visible para otros jugadores"
            {...form.getInputProps('nombreSala')}
            required
          />

          <Group grow>
            <NumberInput
              label="Número de Preguntas"
              min={5}
              max={50}
              description="Solo reactivos (no casos de estudio)"
              {...form.getInputProps('cantidadReactivos')}
              required
            />
            <NumberInput
              label="Máximo Participantes"
              min={2}
              max={10}
              leftSection={<IconUsers size={16} />}
              {...form.getInputProps('maxParticipantes')}
              required
            />
          </Group>

          <NumberInput
            label="Tiempo Límite (minutos)"
            min={10}
            max={120}
            description="Tiempo total para completar el examen"
            leftSection={<IconClock size={16} />}
            {...form.getInputProps('tiempoLimiteMin')}
            required
          />

          {/* Configuración del examen */}
          <Title order={4} className="text-gray-700 mt-4">
            Configuración del Examen
          </Title>

          <MultiSelect
            label="Especialidades"
            placeholder="Selecciona las especialidades"
            data={especialidadOptions}
            description="Los reactivos se tomarán de estas especialidades"
            searchable
            {...form.getInputProps('especialidadIds')}
            required
          />

          <Select
            label="Dificultad"
            data={dificultadOptions}
            description="Nivel de dificultad de las preguntas"
            {...form.getInputProps('dificultadId')}
            required
          />

          {/* Opciones avanzadas */}
          <Card withBorder padding="sm">
            <Switch
              label="Permitir entrada tardía"
              description="Los usuarios pueden unirse después de que inicie el examen"
              {...form.getInputProps('permitirEntradaTardia', { type: 'checkbox' })}
            />
          </Card>

          {/* Botones */}
          <Group justify="flex-end" gap="md" mt="lg">
            <Button
              variant="subtle"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              loading={loading}
              variant="gradient"
              gradient={{ from: 'blue', to: 'cyan' }}
            >
              Crear Sala
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default CrearSalaModal;